import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileText,
  TrendingDown,
  Activity,
  Clock,
  DollarSign,
  Ban,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = window.location.origin || '';

const AdminFraudDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Summary data
  const [summary, setSummary] = useState(null);
  const [summaryDays, setSummaryDays] = useState(30);
  
  // Alerts data
  const [alerts, setAlerts] = useState([]);
  const [alertsPage, setAlertsPage] = useState(1);
  const [alertsPagination, setAlertsPagination] = useState(null);
  const [severityFilter, setSeverityFilter] = useState('');
  
  // Audit logs data
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPagination, setAuditPagination] = useState(null);
  const [actionFilter, setActionFilter] = useState('');
  const [actionTypes, setActionTypes] = useState([]);
  const [searchBookingRef, setSearchBookingRef] = useState('');
  
  // Suspect bookings
  const [suspectBookings, setSuspectBookings] = useState([]);
  const [suspectPage, setSuspectPage] = useState(1);
  const [suspectPagination, setSuspectPagination] = useState(null);
  
  // Price comparison report
  const [priceReport, setPriceReport] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('vipgez_admin_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/fraud/summary?days=${summaryDays}`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setSummary(data.summary);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, [summaryDays]);

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    try {
      let url = `${API_BASE}/api/admin/fraud/alerts?page=${alertsPage}&limit=15`;
      if (severityFilter) url += `&severity=${severityFilter}`;
      
      const response = await fetch(url, { headers: getAuthHeaders() });
      const data = await response.json();
      if (data.success) {
        setAlerts(data.alerts);
        setAlertsPagination(data.pagination);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  }, [alertsPage, severityFilter]);

  // Fetch audit logs
  const fetchAuditLogs = useCallback(async () => {
    try {
      let url = `${API_BASE}/api/admin/fraud/audit-logs?page=${auditPage}&limit=30`;
      if (actionFilter) url += `&action_filter=${actionFilter}`;
      if (searchBookingRef) url += `&booking_ref=${searchBookingRef}`;
      
      const response = await fetch(url, { headers: getAuthHeaders() });
      const data = await response.json();
      if (data.success) {
        setAuditLogs(data.logs);
        setAuditPagination(data.pagination);
        setActionTypes(data.action_types || []);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    }
  }, [auditPage, actionFilter, searchBookingRef]);

  // Fetch suspect bookings
  const fetchSuspectBookings = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/fraud/suspect-bookings?page=${suspectPage}&limit=15`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setSuspectBookings(data.bookings);
        setSuspectPagination(data.pagination);
      }
    } catch (error) {
      console.error('Error fetching suspect bookings:', error);
    }
  }, [suspectPage]);

  // Fetch price comparison report
  const fetchPriceReport = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/fraud/price-comparison?days=7`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setPriceReport(data.report);
      }
    } catch (error) {
      console.error('Error fetching price report:', error);
    }
  }, []);

  // Resolve fraud flag
  const handleResolve = async (bookingRef, resolution) => {
    if (!window.confirm(`Bu rezervasyonu "${resolution === 'cleared' ? 'Temizle' : 'Dolandırıcılık Onayla'}" olarak işaretlemek istediğinize emin misiniz?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/fraud/resolve/${bookingRef}?resolution=${resolution}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Rezervasyon ${resolution === 'cleared' ? 'temizlendi' : 'dolandırıcılık olarak onaylandı'}`);
        fetchSuspectBookings();
        fetchSummary();
      } else {
        toast.error(data.detail || 'İşlem başarısız');
      }
    } catch (error) {
      toast.error('Hata oluştu');
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchSummary(),
        fetchAlerts(),
        fetchPriceReport()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchSummary, fetchAlerts, fetchPriceReport]);

  // Tab change effects
  useEffect(() => {
    if (activeTab === 'alerts') fetchAlerts();
    else if (activeTab === 'audit') fetchAuditLogs();
    else if (activeTab === 'suspects') fetchSuspectBookings();
  }, [activeTab, fetchAlerts, fetchAuditLogs, fetchSuspectBookings]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('tr-TR');
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      warning: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      info: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    };
    const labels = {
      critical: 'Kritik',
      warning: 'Uyarı',
      info: 'Bilgi'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs border ${styles[severity] || styles.info}`}>
        {labels[severity] || severity}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-background flex admin-theme">
      <AdminSidebar />
      
      <main className="flex-1 p-6 ml-64">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-red-500" />
            <div>
              <h1 className="text-2xl font-bold text-white">Fraud Detection Dashboard</h1>
              <p className="text-slate-400 text-sm">Şüpheli işlemler, fiyat uyuşmazlıkları ve güvenlik logları</p>
            </div>
          </div>
          
          <Button 
            onClick={() => {
              fetchSummary();
              fetchAlerts();
              fetchPriceReport();
              toast.success('Veriler yenilendi');
            }}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Yenile
          </Button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-2 text-slate-400 mb-2">
                <FileText className="h-4 w-4" />
                <span className="text-xs">Toplam Quote</span>
              </div>
              <p className="text-2xl font-bold text-white">{summary.total_quotes_created}</p>
            </div>
            
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-2 text-slate-400 mb-2">
                <Activity className="h-4 w-4" />
                <span className="text-xs">Toplam Rezervasyon</span>
              </div>
              <p className="text-2xl font-bold text-white">{summary.total_bookings}</p>
            </div>
            
            <div className="bg-slate-800 rounded-xl p-4 border border-green-500/30">
              <div className="flex items-center gap-2 text-green-400 mb-2">
                <CheckCircle className="h-4 w-4" />
                <span className="text-xs">Doğrulanmış Ödeme</span>
              </div>
              <p className="text-2xl font-bold text-green-400">{summary.verified_payments}</p>
            </div>
            
            <div className="bg-slate-800 rounded-xl p-4 border border-red-500/30">
              <div className="flex items-center gap-2 text-red-400 mb-2">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-xs">Şüpheli İşlem</span>
              </div>
              <p className="text-2xl font-bold text-red-400">{summary.fraud_suspects}</p>
            </div>
            
            <div className="bg-slate-800 rounded-xl p-4 border border-yellow-500/30">
              <div className="flex items-center gap-2 text-yellow-400 mb-2">
                <TrendingDown className="h-4 w-4" />
                <span className="text-xs">Fiyat Uyuşmazlığı</span>
              </div>
              <p className="text-2xl font-bold text-yellow-400">{summary.price_mismatches}</p>
            </div>
            
            <div className="bg-slate-800 rounded-xl p-4 border border-orange-500/30">
              <div className="flex items-center gap-2 text-orange-400 mb-2">
                <Ban className="h-4 w-4" />
                <span className="text-xs">Engellenen Transfer</span>
              </div>
              <p className="text-2xl font-bold text-orange-400">{summary.blocked_provider_trips}</p>
            </div>
          </div>
        )}

        {/* Price Manipulation Report */}
        {priceReport && priceReport.manipulation_attempts > 0 && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-red-500/20 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-400 mb-1">Fiyat Manipülasyonu Tespit Edildi</h3>
                <p className="text-slate-300 text-sm mb-3">
                  Son {priceReport.period_days} günde <strong>{priceReport.manipulation_attempts}</strong> adet fiyat manipülasyonu girişimi engellendi. 
                  Toplam <strong>€{priceReport.total_attempted_fraud.toFixed(2)}</strong> tutarında dolandırıcılık önlendi.
                </p>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  onClick={() => setActiveTab('audit')}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Detayları Gör
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-4">
          {[
            { id: 'overview', label: 'Genel Bakış', icon: Activity },
            { id: 'alerts', label: 'Uyarılar', icon: AlertTriangle },
            { id: 'suspects', label: 'Şüpheli Rezervasyonlar', icon: XCircle },
            { id: 'audit', label: 'Audit Logları', icon: FileText }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-orange-500 text-white'
                  : 'text-slate-400 hover:bg-slate-800'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Recent Alerts */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                Son Uyarılar
              </h3>
              
              {alerts.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
                  <p>Harika! Şu anda aktif uyarı bulunmuyor.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.slice(0, 5).map((alert, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {getSeverityBadge(alert.severity)}
                        <div>
                          <p className="text-white text-sm font-medium">{alert.action.replace(/_/g, ' ')}</p>
                          <p className="text-slate-400 text-xs">
                            {alert.booking_ref && `Ref: ${alert.booking_ref}`}
                            {alert.expected_amount && alert.received_amount && (
                              <span className="ml-2">
                                Beklenen: €{alert.expected_amount} | Alınan: €{alert.received_amount}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <span className="text-slate-500 text-xs">{formatDate(alert.timestamp)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Security Status */}
            <div className="bg-slate-800 rounded-xl p-6 border border-green-500/30">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Shield className="h-5 w-5 text-green-500" />
                Güvenlik Durumu
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                  <span className="text-green-400">Server-side Quote sistemi aktif</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                  <span className="text-green-400">Stripe webhook doğrulaması aktif</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                  <span className="text-green-400">Fiyat manipülasyonu koruması aktif</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                  <span className="text-green-400">Audit logging aktif</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'alerts' && (
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            {/* Filters */}
            <div className="flex gap-4 mb-6">
              <select
                value={severityFilter}
                onChange={(e) => { setSeverityFilter(e.target.value); setAlertsPage(1); }}
                className="bg-slate-700 text-white rounded-lg px-4 py-2 border border-slate-600"
              >
                <option value="">Tüm Seviyeler</option>
                <option value="critical">Kritik</option>
                <option value="warning">Uyarı</option>
                <option value="info">Bilgi</option>
              </select>
            </div>
            
            {/* Alerts Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-slate-400 text-sm border-b border-slate-700">
                    <th className="pb-3 font-medium">Tarih</th>
                    <th className="pb-3 font-medium">Seviye</th>
                    <th className="pb-3 font-medium">İşlem</th>
                    <th className="pb-3 font-medium">Rezervasyon</th>
                    <th className="pb-3 font-medium">Tutar</th>
                    <th className="pb-3 font-medium">Detaylar</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((alert, idx) => (
                    <tr key={idx} className="border-b border-slate-700/50 text-sm">
                      <td className="py-3 text-slate-300">{formatDate(alert.timestamp)}</td>
                      <td className="py-3">{getSeverityBadge(alert.severity)}</td>
                      <td className="py-3 text-white">{alert.action.replace(/_/g, ' ')}</td>
                      <td className="py-3 text-orange-400">{alert.booking_ref || '-'}</td>
                      <td className="py-3 text-white">
                        {alert.expected_amount && (
                          <div>
                            <span className="text-green-400">€{alert.expected_amount}</span>
                            {alert.received_amount && alert.received_amount !== alert.expected_amount && (
                              <span className="text-red-400 ml-2">→ €{alert.received_amount}</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-3 text-slate-400 text-xs max-w-xs truncate">
                        {alert.details?.error || alert.details?.fraud_reason || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {alertsPagination && alertsPagination.pages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <span className="text-slate-400 text-sm">
                  Sayfa {alertsPagination.page} / {alertsPagination.pages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAlertsPage(p => Math.max(1, p - 1))}
                    disabled={alertsPage === 1}
                    className="border-slate-600"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAlertsPage(p => Math.min(alertsPagination.pages, p + 1))}
                    disabled={alertsPage >= alertsPagination.pages}
                    className="border-slate-600"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'suspects' && (
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4">Şüpheli Rezervasyonlar</h3>
            
            {suspectBookings.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
                <p className="text-lg">Şüpheli rezervasyon bulunmuyor</p>
                <p className="text-sm">Tüm işlemler temiz görünüyor.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {suspectBookings.map((booking, idx) => (
                  <div 
                    key={idx}
                    className="bg-slate-700/50 rounded-lg p-4 border border-red-500/30"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-orange-400 font-mono">{booking.booking_ref}</span>
                          <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">
                            {booking.payment_status}
                          </span>
                        </div>
                        <p className="text-white">{booking.customer_name}</p>
                        <p className="text-slate-400 text-sm">{booking.customer_email}</p>
                        
                        {booking.payment_expected && booking.payment_received && (
                          <div className="mt-2 text-sm">
                            <span className="text-slate-400">Beklenen:</span>
                            <span className="text-green-400 ml-2">€{booking.payment_expected}</span>
                            <span className="text-slate-400 mx-2">|</span>
                            <span className="text-slate-400">Alınan:</span>
                            <span className="text-red-400 ml-2">€{booking.payment_received}</span>
                          </div>
                        )}
                        
                        {booking.fraud_reason && (
                          <p className="text-red-400 text-sm mt-1">Sebep: {booking.fraud_reason}</p>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                          onClick={() => handleResolve(booking.booking_ref, 'cleared')}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Temizle
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          onClick={() => handleResolve(booking.booking_ref, 'confirmed')}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Fraud Onayla
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            {/* Filters */}
            <div className="flex gap-4 mb-6">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Rezervasyon Ref ara..."
                  value={searchBookingRef}
                  onChange={(e) => setSearchBookingRef(e.target.value)}
                  className="pl-10 bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <select
                value={actionFilter}
                onChange={(e) => { setActionFilter(e.target.value); setAuditPage(1); }}
                className="bg-slate-700 text-white rounded-lg px-4 py-2 border border-slate-600"
              >
                <option value="">Tüm İşlemler</option>
                {actionTypes.map(type => (
                  <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                ))}
              </select>
              <Button
                variant="outline"
                onClick={() => { setSearchBookingRef(''); setActionFilter(''); fetchAuditLogs(); }}
                className="border-slate-600 text-slate-300"
              >
                <Filter className="h-4 w-4 mr-2" />
                Temizle
              </Button>
            </div>
            
            {/* Audit Logs Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-slate-400 text-sm border-b border-slate-700">
                    <th className="pb-3 font-medium">Tarih</th>
                    <th className="pb-3 font-medium">İşlem</th>
                    <th className="pb-3 font-medium">Rezervasyon</th>
                    <th className="pb-3 font-medium">Rota</th>
                    <th className="pb-3 font-medium">Tutar</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log, idx) => (
                    <tr key={idx} className="border-b border-slate-700/50 text-sm">
                      <td className="py-3 text-slate-300">{formatDate(log.timestamp)}</td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          log.action.includes('FRAUD') ? 'bg-red-500/20 text-red-400' :
                          log.action.includes('verified') ? 'bg-green-500/20 text-green-400' :
                          'bg-slate-600 text-slate-300'
                        }`}>
                          {log.action.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="py-3 text-orange-400 font-mono text-xs">{log.booking_ref || '-'}</td>
                      <td className="py-3 text-slate-300 text-xs max-w-[250px]">
                        {log.route ? (
                          <div className="truncate" title={`${log.route.pickup} → ${log.route.dropoff}`}>
                            <span className="text-green-400">{log.route.pickup?.slice(0, 20)}</span>
                            <span className="text-slate-500 mx-1">→</span>
                            <span className="text-blue-400">{log.route.dropoff?.slice(0, 20)}</span>
                            {log.route.pickup_date && (
                              <span className="text-slate-500 ml-2 text-[10px]">
                                ({log.route.pickup_date} {log.route.pickup_time})
                              </span>
                            )}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="py-3 text-white">
                        {log.amount ? `€${log.amount}` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {auditPagination && auditPagination.pages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <span className="text-slate-400 text-sm">
                  Toplam {auditPagination.total} kayıt | Sayfa {auditPagination.page} / {auditPagination.pages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAuditPage(p => Math.max(1, p - 1))}
                    disabled={auditPage === 1}
                    className="border-slate-600"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAuditPage(p => Math.min(auditPagination.pages, p + 1))}
                    disabled={auditPage >= auditPagination.pages}
                    className="border-slate-600"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminFraudDashboard;
